/**
 * TR-6: client for the Training digest settings (Settings → Training). Talks to the
 * company-scoped-admin endpoints under /api/v1/admin/training. The server owns
 * authorization + validation; this is a thin typed wrapper.
 */
import { adminFetch } from './adminApi';

export interface TrainingSettingsRow {
  id: string;
  company_id: string | null;
  enabled: boolean;
  timezone: string;
  send_weekday: number; // 0=Sun … 6=Sat
  send_time: string; // 'HH:MM:SS'
  report_passed: boolean;
  report_threshold: boolean;
  threshold_pct: number;
  report_all_tests_complete: boolean;
  report_last_used: boolean;
  inactivity_days: number;
  notify_company_admins: boolean;
  notify_superadmins: boolean;
  recipient_emails: string[];
  updated_at?: string;
  updated_by?: string | null;
}

export interface TrainingSettingsResponse {
  company_id: string | null;
  effective: TrainingSettingsRow;
  source: 'company' | 'default';
  is_superadmin: boolean;
  default?: TrainingSettingsRow;
}

export type TrainingSettingsPatch = Partial<
  Pick<
    TrainingSettingsRow,
    | 'enabled'
    | 'timezone'
    | 'send_weekday'
    | 'send_time'
    | 'report_passed'
    | 'report_threshold'
    | 'threshold_pct'
    | 'report_all_tests_complete'
    | 'report_last_used'
    | 'inactivity_days'
    | 'notify_company_admins'
    | 'notify_superadmins'
    | 'recipient_emails'
  >
>;

export async function getTrainingSettings(): Promise<TrainingSettingsResponse> {
  return adminFetch<TrainingSettingsResponse>('/training/settings');
}

export async function saveTrainingSettings(
  patch: TrainingSettingsPatch,
  scope: 'company' | 'default' = 'company',
): Promise<{ effective: TrainingSettingsRow; scope: 'company' | 'default' }> {
  return adminFetch('/training/settings', {
    method: 'PUT',
    body: JSON.stringify({ scope, ...patch }),
  });
}
